<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import KanbanAPI from '../../../../api/kanban';
import WootMessageEditor from '../../../../components/widgets/WootWriter/Editor.vue';
import WootInput from '../../../../../v3/components/Form/Input.vue';
import WootToggle from './Toggle.vue';
import WootSelect from '../../../../../v3/components/Form/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import FunnelAPI from '../../../../api/funnel';
import TemplatesPicker from '../../../../components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import PhoneInput from 'dashboard/components/widgets/forms/PhoneInput.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';


const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({}),
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  funnels: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const store = useStore();
const emit = defineEmits(['saved', 'close']);
const isLoading = ref(false);
const error = ref(null);
const errors = ref({});
const kanbanItems = ref([]);

// Estado para controlar quando os dados estão prontos
const isDataReady = ref(false);

// Buscar todos os funis ao montar o componente
onMounted(async () => {
  try {
    isLoading.value = true;
    // Dispara a action para buscar todos os funis
    await store.dispatch('funnel/fetch');
    // Buscar atributos personalizados para os placeholders
    await store.dispatch('attributes/get');
    // Buscar labels da conta para condições
    await store.dispatch('labels/get');
    // Buscar agentes da conta para condições
    await store.dispatch('agents/get');
  } catch (error) {
    console.error('Erro ao carregar funis:', error);
  } finally {
    isLoading.value = false;
  }
});

// Computed para obter todos os funis
const funnels = computed(() => props.funnels);

// Computed para obter o funil selecionado
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

// Função para garantir que o valor é uma string
const ensureString = value => (value ? String(value) : '');

// Computed para obter as etapas do funil selecionado
const funnelStages = computed(() => {
  // Busca o funil diretamente dos records usando o ID atual
  const currentFunnel = funnels.value.find(
    f => String(f.id) === formData.value.funnel_id
  );

  let stages = [];

  if (currentFunnel?.stages) {
    stages = Object.entries(currentFunnel.stages)
      .map(([id, stage]) => ({
        value: String(id),
        label: stage.name,
        position: stage.position,
      }))
      .sort((a, b) => a.position - b.position);
  }

  // Se estamos editando e a etapa atual não está na lista, adiciona ela
  if (props.isEditing && props.initialData.stage_id) {
    const currentStageId = String(props.initialData.stage_id);
    const stageExists = stages.some(stage => stage.value === currentStageId);

    if (!stageExists) {
      // Adiciona a etapa atual como opção temporária
      stages.unshift({
        value: currentStageId,
        label: `Etapa ${currentStageId}`, // Label temporário
        position: 0,
      });
    }
  }

  return stages;
});

// Computed para transformar os funis em options para o select
const funnelOptions = computed(() => {
  const options = funnels.value.map(funnel => ({
    value: String(funnel.id),
    label: funnel.name,
  }));

  // Se estamos editando e o funil atual não está na lista, adiciona ele
  if (props.isEditing && props.initialData.funnel_id) {
    const currentFunnelId = String(props.initialData.funnel_id);
    const funnelExists = options.some(
      option => option.value === currentFunnelId
    );

    if (!funnelExists) {
      // Adiciona o funil atual como opção temporária
      options.unshift({
        value: currentFunnelId,
        label: `Funil ${currentFunnelId}`, // Label temporário
      });
    }
  }

  return options;
});

// Form data atualizado para incluir o funil_id e templates WhatsApp
const formData = ref({
  funnel_id: ensureString(props.initialData.funnel_id),
  stage_id: ensureString(props.initialData.stage_id),
  title: props.initialData.title || '',
  content: props.initialData.content || '',
  is_default: props.initialData.is_default || false,
  webhook: {
    enabled: props.initialData.webhook?.enabled || false,
    url: props.initialData.webhook?.url || '',
    method: props.initialData.webhook?.method || 'POST',
  },
  conditions: {
    enabled: props.initialData.conditions?.enabled || false,
    rules: props.initialData.conditions?.rules || [],
  },
  // Templates oficiais do WhatsApp - um por canal
  whatsapp_templates: props.initialData.whatsapp_templates || [],
  // Stage Notifications — send WhatsApp to external numbers on stage entry
  stage_notifications: {
    enabled:
      props.initialData.stage_notifications?.enabled || false,
    rate_limit_waha:
      props.initialData.stage_notifications?.rate_limit_waha ??
      props.initialData.stage_notifications?.rate_limit ?? 10,
    rate_limit_official:
      props.initialData.stage_notifications?.rate_limit_official ?? 50,
    recipients:
      props.initialData.stage_notifications?.recipients || [],
  },
});

// Validation
const validateForm = () => {
  errors.value = {};

  if (!selectedFunnel.value?.id) {
    errors.value.funnel = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.FUNNEL_REQUIRED'
    );
  }

  if (!formData.value.stage_id) {
    errors.value.stage = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.STAGE_REQUIRED'
    );
  }

  // Check if stage notifications alone justify saving (no need for title/content)
  const hasActiveStageNotifications =
    formData.value.stage_notifications?.enabled === true &&
    formData.value.stage_notifications?.recipients?.length > 0;

  // Conteúdo só é obrigatório se NÃO houver template WhatsApp configurado
  // E NÃO houver notificações de etapa ativas
  const hasWhatsappTemplateConfigured = formData.value.whatsapp_templates?.some(
    wt => wt.enabled && wt.template_name
  );

  const hasAlternativeConfig = hasWhatsappTemplateConfigured || hasActiveStageNotifications;

  if (!formData.value.title.trim() && !hasAlternativeConfig) {
    errors.value.title = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.TITLE_REQUIRED'
    );
  }

  if (!formData.value.content.trim() && !hasAlternativeConfig) {
    errors.value.content = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.CONTENT_REQUIRED'
    );
  }

  if (formData.value.webhook.enabled && !formData.value.webhook.url.trim()) {
    errors.value.webhookUrl = t(
      'KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.WEBHOOK_URL_REQUIRED'
    );
  }

  return Object.keys(errors.value).length === 0;
};

// Atualiza o handler de submit para incluir o funil e etapa
const handleSubmit = async () => {
  try {
    if (!validateForm()) return;

    isLoading.value = true;
    const currentFunnel = funnels.value.find(
      f => String(f.id) === formData.value.funnel_id
    );

    if (!currentFunnel) {
      throw new Error('Funil não encontrado');
    }

    // Cria uma cópia do funil mantendo a estrutura original
    const updatedFunnel = {
      ...currentFunnel,
      stages: { ...currentFunnel.stages }, // Clona stages para preservar IDs
    };

    const selectedStageId = formData.value.stage_id;
    const currentStage = updatedFunnel.stages[selectedStageId];

    // Preserva a estrutura existente da etapa
    updatedFunnel.stages[selectedStageId] = {
      ...currentStage,
      message_templates: [...(currentStage?.message_templates || [])],
      // Stage notifications lives at the stage level, not inside templates
      stage_notifications: formData.value.stage_notifications,
    };

    // Exclude stage_notifications from template entry — it's a stage-level property
    // eslint-disable-next-line no-unused-vars
    const { stage_notifications: _sn, ...templateData } = formData.value;

    if (props.isEditing) {
      const templateIndex = updatedFunnel.stages[
        selectedStageId
      ].message_templates.findIndex(t => t.id === props.initialData.id);

      if (templateIndex > -1) {
        // Strip control properties that shouldn't persist in template JSONB entry
        // eslint-disable-next-line no-unused-vars
        const { stage_notifications: _sn2, isEditing: _ie, ...cleanInitial } = props.initialData;

        updatedFunnel.stages[selectedStageId].message_templates[templateIndex] =
          {
            ...cleanInitial,
            ...templateData,
            updated_at: new Date().toISOString(),
          };
      }
    } else {
      updatedFunnel.stages[selectedStageId].message_templates.push({
        id: Date.now(),
        ...templateData,
        created_at: new Date().toISOString(),
      });
    }

    await FunnelAPI.update(currentFunnel.id, updatedFunnel);
    await store.dispatch('funnel/fetch');
    emit('saved', formData.value);
  } catch (error) {
    errors.value = {
      submit: t('KANBAN.MESSAGE_TEMPLATES.FORM.ERRORS.SAVE_ERROR'),
    };
  } finally {
    isLoading.value = false;
  }
};

// Busca os itens do Kanban para extrair os campos disponíveis
const fetchKanbanItems = async () => {
  try {
    isLoading.value = true;
    const currentFunnel = store.getters['funnel/getSelectedFunnel'];

    if (!currentFunnel?.id) {
      console.error('Nenhum funil selecionado');
      return;
    }

    const response = await KanbanAPI.getItems(currentFunnel.id);

    // Verifica se response.data é um array antes de usar .map()
    if (Array.isArray(response.data)) {
      kanbanItems.value = response.data.map(item => ({
        id: item.id,
        title: item.item_details.title || '',
        description: item.item_details.description || '',
        priority: item.item_details.priority || 'none',
        assignee: item.item_details.assignee || null,
        position: item.position,
        funnel_stage: item.funnel_stage,
        item_details: item.item_details,
        custom_attributes: item.custom_attributes || {},
        createdAt: new Date(item.created_at).toLocaleDateString(),
      }));
    } else {
      console.warn('Response.data não é um array:', response.data);
      kanbanItems.value = [];
    }
  } catch (error) {
    console.error('Erro ao carregar itens do Kanban:', error);
    kanbanItems.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Computed para extrair campos únicos dos itens
const availableFields = computed(() => {
  return [
    { value: 'item_details.title', label: 'Título' },
    { value: 'item_details.description', label: 'Descrição' },
    { value: 'item_details.priority', label: 'Prioridade' },
    { value: 'item_details.agent_id', label: 'Atendente' },
    { value: 'item_details.conversation_id', label: 'Conversa' },
    { value: 'item_details.value', label: 'Valor' },
    { value: 'funnel_stage', label: 'Etapa do Funil' },
    { value: 'label', label: 'Etiqueta' },
  ].sort((a, b) => a.label.localeCompare(b.label));
});

// Computed para obter as labels da conta
const accountLabels = computed(() => store.getters['labels/getLabels'] || []);

// Computed para opções de labels no dropdown de condições
const labelOptions = computed(() =>
  accountLabels.value.map(label => ({
    value: label.title,
    label: label.title,
    color: label.color,
  }))
);

// Computed para obter todos os agentes da conta
const accountAgents = computed(() => store.getters['agents/getAgents'] || []);

// Computed para opções de agentes no dropdown de condições
// Filtra pelos agentes vinculados ao funil selecionado
const agentOptions = computed(() => {
  const currentFunnel = funnels.value.find(
    f => String(f.id) === formData.value.funnel_id
  );
  
  // Extrai os IDs dos agentes do funil (settings.agents)
  // Guard: filtra valores inválidos (NaN, null, undefined)
  let funnelAgentIds = [];
  if (currentFunnel?.settings?.agents && Array.isArray(currentFunnel.settings.agents)) {
    funnelAgentIds = currentFunnel.settings.agents
      .map(a => {
        if (typeof a === 'object' && a !== null) return a.id;
        return a;
      })
      .map(id => Number(id))
      .filter(id => !Number.isNaN(id) && id > 0); // Remove NaN e IDs inválidos
  }
  
  // Se não há agentes no funil, mostra todos
  // Se há agentes no funil, filtra apenas esses
  const agentsToShow = funnelAgentIds.length > 0
    ? accountAgents.value.filter(agent => funnelAgentIds.includes(agent.id))
    : accountAgents.value;
  
  return agentsToShow.map(agent => ({
    value: String(agent.id),
    label: agent.name,
  }));
});

// Computed para o v-model do funnel_id
const selectedFunnelId = computed({
  get: () => {
    return formData.value.funnel_id;
  },
  set: async value => {
    formData.value.funnel_id = ensureString(value);
    await handleFunnelChange(value);
  },
});

// Computed para o v-model do stage_id
const selectedStageId = computed({
  get: () => formData.value.stage_id,
  set: value => {
    formData.value.stage_id = ensureString(value);
  },
});

// Available HTTP methods for webhook
const httpMethods = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

// Computed para controlar a exibição do editor de regras condicionais
const showConditionsEditor = computed(() => formData.value.conditions.enabled);

const addConditionRule = () => {
  formData.value.conditions.rules.push({
    field: '',
    operator: 'equals',
    value: '',
  });
};

const removeConditionRule = index => {
  formData.value.conditions.rules.splice(index, 1);
};

// Handler para limpar valor quando campo muda (evita valores inválidos)
const handleConditionFieldChange = (rule, newField) => {
  rule.field = newField;
  rule.value = ''; // Limpa o valor ao trocar de campo
};

// Handler para quando um funil é selecionado
const handleFunnelChange = async value => {
  try {
    // Atualiza o ID do funil no formData
    formData.value.funnel_id = ensureString(value);

    // Busca o funil completo dos records do store
    const selectedFunnel = funnels.value.find(
      f => String(f.id) === formData.value.funnel_id
    );

    if (selectedFunnel) {
      // Atualiza o funil selecionado no store
      await store.dispatch('funnel/setSelectedFunnel', selectedFunnel);

      await nextTick();

      // Verifica se a etapa atual existe no novo funil
      const stages = Object.keys(selectedFunnel.stages || {});
      const currentStageId = formData.value.stage_id;
      const stageExists = stages.some(id => String(id) === currentStageId);

      // Só limpa a etapa se não estiver editando ou se a etapa não existir no novo funil
      if (!props.isEditing || !stageExists) {
        formData.value.stage_id = stages[0] || '';
      }
    }
  } catch (error) {
    console.error('Erro ao mudar funil:', error);
  }
};

// Carrega os itens quando o componente é montado
onMounted(async () => {
  try {
    isLoading.value = true;

    // Aguarda o DOM atualizar
    await nextTick();

    // Se houver um funil_id inicial, seleciona ele
    if (props.initialData.funnel_id) {
      const funnel = funnels.value.find(
        f => String(f.id) === ensureString(props.initialData.funnel_id)
      );

      if (funnel) {
        // Atualiza o funil selecionado no store
        await store.dispatch('funnel/setSelectedFunnel', funnel);

        // Verifica se a stage_id existe nas stages do funil
        const stages = Object.keys(funnel.stages || {});
        const initialStageId = ensureString(props.initialData.stage_id);
        const stageExists = stages.some(id => String(id) === initialStageId);

        // Aguarda o DOM atualizar
        await nextTick();

        // Força a atualização dos selects
        if (!stageExists) {
          formData.value.stage_id = stages[0] || '';
        }
      }
    }

    await nextTick();
    isDataReady.value = true;
  } catch (error) {
    console.error('Erro ao inicializar formulário:', error);
  } finally {
    isLoading.value = false;
  }
});

// ===== WhatsApp Templates Section =====

// Computed para obter os inboxes do WhatsApp da conta
const whatsappInboxes = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  return inboxes.filter(inbox => inbox.channel_type === INBOX_TYPES.WHATSAPP);
});

// Computed para inbox IDs já configurados
const configuredInboxIds = computed(() => {
  return formData.value.whatsapp_templates.map(wt => wt.inbox_id);
});

// Computed para inboxes disponíveis (não configurados ainda)
const availableWhatsappInboxes = computed(() => {
  return whatsappInboxes.value.filter(
    inbox => !configuredInboxIds.value.includes(inbox.id)
  );
});

// ===== Stage Notifications (WhatsApp Web + WhatsApp Official) =====

// Computed: filter only WhatsApp Web inboxes (Channel::Api with platform_type=whatsapp_web)
// Note: the API serializer puts channel.additional_attributes directly at inbox level
const wahaInboxes = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  return inboxes.filter(
    inbox =>
      inbox.channel_type === INBOX_TYPES.API &&
      inbox.additional_attributes?.platform_type === 'whatsapp_web'
  );
});

// Computed: WhatsApp Official inboxes (Channel::Whatsapp)
const whatsappOfficialInboxes = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  return inboxes.filter(
    inbox => inbox.channel_type === INBOX_TYPES.WHATSAPP
  );
});

// Show notifications section if there are ANY notification-capable inboxes
const hasNotificationInboxes = computed(
  () => wahaInboxes.value.length > 0 || whatsappOfficialInboxes.value.length > 0
);

// Get inboxes for a specific recipient type
const getInboxesForType = type => {
  if (type === 'whatsapp_official') return whatsappOfficialInboxes.value;
  return wahaInboxes.value;
};

// Notification type options for dropdown
const notificationTypeOptions = computed(() => {
  const options = [];
  if (wahaInboxes.value.length > 0) {
    options.push({ value: 'waha', label: 'WhatsApp Web' });
  }
  if (whatsappOfficialInboxes.value.length > 0) {
    options.push({ value: 'whatsapp_official', label: 'WhatsApp Oficial (API)' });
  }
  return options;
});

const addNotificationRecipient = () => {
  // Default to first available type
  const defaultType = wahaInboxes.value.length > 0 ? 'waha' : 'whatsapp_official';
  const defaultInboxes = getInboxesForType(defaultType);
  formData.value.stage_notifications.recipients.push({
    id: `notif_${Date.now()}`,
    type: defaultType,
    phone_number: '',
    label: '',
    inbox_id: defaultInboxes[0]?.id || null,
    message: '',
    // WhatsApp Official fields (only used when type=whatsapp_official)
    template_name: '',
    template_language: '',
    template_namespace: '',
    template_body: '',
    variable_mapping: {},
    header_media_url: '',
    header_media_type: 'image',
    header_text_variable: '',
  });
};

const removeNotificationRecipient = index => {
  formData.value.stage_notifications.recipients.splice(index, 1);
};

// Handle phone number update from PhoneInput component
// PhoneInput emits the local number (without DDI), we compose the full number
const recipientDialCodes = ref({});

const onRecipientPhoneInput = (rIndex, localNumber) => {
  const recipient = formData.value.stage_notifications.recipients[rIndex];
  if (!recipient) return;
  const dialCode = recipientDialCodes.value[rIndex] || '';
  recipient.phone_number = dialCode ? `${dialCode}${localNumber}` : localNumber;
};

const onRecipientSetCode = (rIndex, dialCode) => {
  recipientDialCodes.value[rIndex] = dialCode;
  // Re-compose the full number with the new dial code
  const recipient = formData.value.stage_notifications.recipients[rIndex];
  if (!recipient) return;
  // Extract local part from current phone_number
  const currentDdi = Object.values(recipientDialCodes.value).find(code =>
    code && recipient.phone_number.startsWith(code)
  );
  const localPart = currentDdi
    ? recipient.phone_number.replace(currentDdi, '')
    : recipient.phone_number.replace(/^\+\d{1,4}/, '');
  recipient.phone_number = dialCode ? `${dialCode}${localPart}` : localPart;
};

const insertNotificationVariable = (recipientIndex, variable) => {
  const recipient =
    formData.value.stage_notifications.recipients[recipientIndex];
  if (!recipient) return;
  recipient.message += `{{${variable}}}`;
};

// Handle type change for a recipient — reset inbox and template fields
const handleRecipientTypeChange = (rIndex, newType) => {
  const recipient = formData.value.stage_notifications.recipients[rIndex];
  if (!recipient) return;
  recipient.type = newType;
  // Reset inbox to first available for the new type
  const inboxes = getInboxesForType(newType);
  recipient.inbox_id = inboxes[0]?.id || null;
  // Reset template fields when switching types
  if (newType === 'waha') {
    recipient.template_name = '';
    recipient.template_language = '';
    recipient.template_namespace = '';
    recipient.template_body = '';
    recipient.variable_mapping = {};
  } else {
    recipient.message = '';
  }
};

// State for notification template picker modal (separate from the main template picker)
const showNotifTemplatePickerModal = ref(false);
const currentNotifRecipientIndex = ref(null);

const openNotifTemplatePicker = rIndex => {
  currentNotifRecipientIndex.value = rIndex;
  showNotifTemplatePickerModal.value = true;
};

const onNotifTemplateSelected = template => {
  const rIndex = currentNotifRecipientIndex.value;
  if (rIndex === null) return;
  const recipient = formData.value.stage_notifications.recipients[rIndex];
  if (!recipient) return;

  recipient.template_name = template.name;
  recipient.template_language = template.language;
  recipient.template_namespace = template.namespace || '';

  // Extract variables from template body
  const bodyComponent = template.components?.find(c => c.type === 'BODY');
  if (bodyComponent) {
    recipient.template_body = bodyComponent.text || '';
    const matches = bodyComponent.text?.match(/\{\{([^}]+)\}\}/g) || [];
    const newMapping = {};
    matches.forEach(match => {
      const varName = match.replace(/\{\{|\}\}/g, '').trim();
      newMapping[varName] = recipient.variable_mapping?.[varName] || '';
    });
    recipient.variable_mapping = newMapping;
  }

  showNotifTemplatePickerModal.value = false;
};

// Get template variables for a notification recipient
const getNotifRecipientVariables = rIndex => {
  const recipient = formData.value.stage_notifications.recipients[rIndex];
  if (!recipient?.variable_mapping) return [];
  return Object.keys(recipient.variable_mapping).map(key => ({
    key,
    value: recipient.variable_mapping[key],
  }));
};

// Update variable mapping for a notification recipient
const updateNotifVariableMapping = (rIndex, varKey, placeholder) => {
  const recipient = formData.value.stage_notifications.recipients[rIndex];
  if (!recipient) return;
  if (!recipient.variable_mapping) recipient.variable_mapping = {};
  recipient.variable_mapping[varKey] = placeholder;
};

const handleNotifVariableSelectChange = (rIndex, varKey, selectedValue) => {
  if (selectedValue === 'MANUAL_INPUT') {
    updateNotifVariableMapping(rIndex, varKey, 'manual:');
  } else {
    updateNotifVariableMapping(rIndex, varKey, selectedValue);
  }
};

// Estado para controlar modal de seleção de template
const showTemplatePickerModal = ref(false);
const currentWaTemplateIndex = ref(null);
const selectedTemplate = ref(null);
const selectedInboxToAdd = ref('');

// Computed para obter atributos personalizados da store
const customAttributes = computed(() => {
  return store.getters['attributes/getAttributes'] || [];
});

// Opções de placeholders para variáveis dinâmicas (agora computed para incluir custom attributes)
const variablePlaceholderOptions = computed(() => {
  // Opções padrão
  const standardOptions = [
    { value: 'contact.name', label: 'Nome do Contato' },
    { value: 'contact.phone_number', label: 'Telefone do Contato' },
    { value: 'contact.email', label: 'Email do Contato' },
    { value: 'conversation.id', label: 'ID da Conversa' },
    { value: 'item_details.title', label: 'Título do Item' },
    { value: 'item_details.value', label: 'Valor do Item' },
    { value: 'item_details.priority', label: 'Prioridade' },
    { value: 'funnel.name', label: 'Nome do Funil' },
    { value: 'funnel.stage_name', label: 'Nome da Etapa' },
    { value: 'agent.name', label: 'Nome do Agente' },
  ];

  // Adicionar atributos personalizados
  const customOptions = customAttributes.value.map(attr => {
    const prefix = attr.attribute_model === 'conversation_attribute' 
      ? 'conversation' 
      : 'contact';
    return {
      value: `${prefix}.custom_attribute.${attr.attribute_key}`,
      label: `${attr.attribute_display_name || attr.attribute_key} (${prefix === 'contact' ? 'Contato' : 'Conversa'})`,
    };
  });

  // Opção para input manual no início
  const manualOption = [
    { value: 'MANUAL_INPUT', label: '✏️ Digitar valor manualmente...' },
  ];

  return [...manualOption, ...standardOptions, ...customOptions];
});

// Adicionar configuração de WhatsApp template para um inbox
const addWhatsappTemplateConfig = inboxId => {
  // Guard: só continua se inboxId é válido
  const parsedId = Number(inboxId);
  if (!parsedId || Number.isNaN(parsedId)) return;

  const inbox = whatsappInboxes.value.find(i => i.id === parsedId);
  if (!inbox) return;

  formData.value.whatsapp_templates.push({
    inbox_id: inbox.id,
    inbox_name: inbox.name,
    enabled: true,
    is_default: formData.value.whatsapp_templates.length === 0, // Primeiro é padrão
    template_name: '',
    template_language: '',
    template_namespace: '',
    template_id: '',
    variable_mapping: {},
    header_media_url: '',
    header_media_type: 'image',
    header_text_variable: '',
  });

  // Reset seleção após adicionar
  selectedInboxToAdd.value = '';
};

// Remover configuração de WhatsApp template
const removeWhatsappTemplateConfig = index => {
  formData.value.whatsapp_templates.splice(index, 1);
};

// Abrir picker de templates para um índice específico
const openTemplatePicker = index => {
  currentWaTemplateIndex.value = index;
  showTemplatePickerModal.value = true;
};

// Quando um template é selecionado no picker
const onTemplateSelected = template => {
  if (currentWaTemplateIndex.value !== null) {
    const waConfig = formData.value.whatsapp_templates[currentWaTemplateIndex.value];
    waConfig.template_name = template.name;
    waConfig.template_language = template.language;
    waConfig.template_id = template.id;
    waConfig.template_namespace = template.namespace || '';
    
    // Extrair variáveis do template para configurar mapeamento
    const bodyComponent = template.components?.find(c => c.type === 'BODY');
    if (bodyComponent) {
      // Salvar o texto do body para exibição no histórico da conversa
      waConfig.template_body = bodyComponent.text || '';
      
      // Identificar placeholders {{1}}, {{2}}, {{primeiro_nome}}, {{pedido_id}}, etc.
      // Regex atualizado para capturar variáveis nomeadas e numéricas
      const matches = bodyComponent.text?.match(/\{\{([^}]+)\}\}/g) || [];
      const newMapping = {};
      matches.forEach(match => {
        const varName = match.replace(/\{\{|\}\}/g, '').trim();
        // Manter mapeamento existente ou deixar vazio
        newMapping[varName] = waConfig.variable_mapping?.[varName] || '';
      });
      waConfig.variable_mapping = newMapping;
    }
    
    selectedTemplate.value = template;
  }
  showTemplatePickerModal.value = false;
};

// Alternar template como padrão para o inbox
const toggleDefaultWhatsappTemplate = index => {
  // Se está ativando como padrão, desativa outros do mesmo inbox
  const current = formData.value.whatsapp_templates[index];
  if (!current.is_default) {
    current.is_default = true;
  } else {
    current.is_default = false;
  }
};

// Computed para verificar se há templates de variáveis para um índice
const getTemplateVariables = index => {
  const waConfig = formData.value.whatsapp_templates[index];
  if (!waConfig?.variable_mapping) return [];
  
  return Object.keys(waConfig.variable_mapping).map(key => ({
    key,
    value: waConfig.variable_mapping[key],
  }));
};

// Atualizar mapeamento de variável
const updateVariableMapping = (waIndex, varKey, placeholder) => {
  const waTemplates = formData.value.whatsapp_templates;
  // Guard: verificar se o template existe
  if (!waTemplates || !waTemplates[waIndex]) return;
  
  // Inicializar variable_mapping se não existir
  if (!waTemplates[waIndex].variable_mapping) {
    waTemplates[waIndex].variable_mapping = {};
  }
  
  waTemplates[waIndex].variable_mapping[varKey] = placeholder;
};

// Handler para mudança de select - trata caso especial de MANUAL_INPUT
const handleVariableSelectChange = (waIndex, varKey, selectedValue) => {
  if (selectedValue === 'MANUAL_INPUT') {
    // Se selecionou input manual, define prefixo 'manual:' para ativar o modo de input
    updateVariableMapping(waIndex, varKey, 'manual:');
  } else {
    updateVariableMapping(waIndex, varKey, selectedValue);
  }
};

// Formatar label da variável para exibição (evita problema de parsing com chaves duplas)
const formatVariableLabel = key => `{{${key}}}`;
</script>


<template>
  <form class="message-template-form" @submit.prevent="handleSubmit">
    <h3 class="text-lg font-medium mb-4">
      {{
        t(
          isEditing
            ? 'KANBAN.MESSAGE_TEMPLATES.EDIT.TITLE'
            : 'KANBAN.MESSAGE_TEMPLATES.NEW'
        )
      }}
    </h3>
    <div class="form-grid">
      <!-- Coluna Esquerda -->
      <div class="left-column">
        <!-- Seleção de Etapa do Funil -->
        <div class="form-section">
          <h3 class="section-title">
            {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.FUNNEL_INFO') }}
          </h3>
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-slate-700 dark:text-slate-300">{{
              t('KANBAN.MESSAGE_TEMPLATES.FORM.DEFAULT_MESSAGE')
            }}</span>
            <WootToggle v-model="formData.is_default" />
          </div>

          <!-- Seletores -->
          <div class="mb-2">
            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.FUNNEL.LABEL') }}
              </label>
              <select
                v-model="selectedFunnelId"
                class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:border-slate-600"
              >
                <option value="" disabled>
                  {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.FUNNEL.PLACEHOLDER') }}
                </option>
                <option
                  v-for="option in funnelOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.STAGE.LABEL') }}
              </label>
              <select
                v-model="selectedStageId"
                class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:border-slate-600"
              >
                <option value="" disabled>
                  {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.STAGE.PLACEHOLDER') }}
                </option>
                <option
                  v-for="option in funnelStages"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Informações Básicas (apenas título) -->
        <div class="form-section">
          <h3 class="section-title">
            {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.BASIC_INFO') }}
          </h3>

          <WootInput
            v-model="formData.title"
            name="title"
            :label="t('KANBAN.MESSAGE_TEMPLATES.FORM.TITLE.LABEL')"
            :placeholder="t('KANBAN.MESSAGE_TEMPLATES.FORM.TITLE.PLACEHOLDER')"
            :error="errors.title"
            class="mb-4"
          />
        </div>

        <!-- Templates Oficiais do WhatsApp -->
        <div v-if="whatsappInboxes.length > 0" class="form-section">
          <div class="flex items-center justify-between mb-4">
            <h3 class="section-title mb-0">
              <fluent-icon
                icon="whatsapp"
                size="18"
                class="inline-block mr-2 text-green-500"
                :icons="dashboardIcons"
              />
              Templates WhatsApp Oficial
            </h3>
          </div>

          <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Configure templates oficiais do WhatsApp para enviar automaticamente
            quando o item entrar nesta etapa (funciona mesmo após 24h).
          </p>

          <!-- Lista de templates configurados -->
          <div
            v-for="(waTemplate, wIndex) in formData.whatsapp_templates"
            :key="wIndex"
            class="whatsapp-template-config mb-4"
          >
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <span class="font-medium text-slate-700 dark:text-slate-300">
                  {{ waTemplate.inbox_name }}
                </span>
                <span
                  v-if="waTemplate.is_default"
                  class="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full dark:bg-green-900 dark:text-green-300"
                >
                  Padrão
                </span>
              </div>
              <div class="flex items-center gap-2">
                <label class="flex items-center gap-1 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="waTemplate.is_default"
                    class="rounded text-green-500"
                  />
                  Padrão
                </label>
                <Button
                  variant="ghost"
                  color="ruby"
                  size="xs"
                  @click="removeWhatsappTemplateConfig(wIndex)"
                >
                  <fluent-icon icon="delete" size="14" :icons="dashboardIcons" />
                </Button>
              </div>
            </div>

            <!-- Seleção do Template -->
            <div class="mb-3">
              <label class="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                Template
              </label>
              <div class="flex gap-2">
                <input
                  type="text"
                  :value="waTemplate.template_name || 'Nenhum template selecionado'"
                  readonly
                  class="flex-1 px-3 py-2 border border-slate-300 rounded-lg bg-slate-100 dark:bg-slate-700 dark:border-slate-600 text-slate-600 dark:text-slate-300"
                />
                <Button
                  variant="smooth"
                  color="blue"
                  size="sm"
                  @click="openTemplatePicker(wIndex)"
                >
                  Selecionar
                </Button>
              </div>
              <p
                v-if="waTemplate.template_language"
                class="text-xs text-slate-500 mt-1"
              >
                Idioma: {{ waTemplate.template_language }}
              </p>
            </div>

            <!-- Mapeamento de Variáveis -->
            <div
              v-if="getTemplateVariables(wIndex).length > 0"
              class="variable-mapping mt-3"
            >
              <label class="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                Variáveis do Template
              </label>
              <div
                v-for="variable in getTemplateVariables(wIndex)"
                :key="variable.key"
                class="flex items-center gap-2 mb-2"
              >
                <span class="text-sm font-mono bg-slate-200 dark:bg-slate-600 px-2 py-1 rounded">
                  {{ formatVariableLabel(variable.key) }}
                </span>
                <span class="text-slate-400">→</span>
                
                <!-- Se o valor atual começa com 'manual:', mostra input -->
                <template v-if="variable.value && variable.value.startsWith('manual:')">
                  <input
                    type="text"
                    :value="variable.value.replace('manual:', '')"
                    @input="updateVariableMapping(wIndex, variable.key, 'manual:' + $event.target.value)"
                    placeholder="Digite o valor fixo..."
                    class="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm dark:bg-slate-700 dark:border-slate-600"
                  />
                  <button
                    type="button"
                    class="px-2 py-1 text-xs bg-slate-200 dark:bg-slate-600 rounded hover:bg-slate-300 dark:hover:bg-slate-500"
                    title="Voltar para seleção"
                    @click="updateVariableMapping(wIndex, variable.key, '')"
                  >
                    ↩️
                  </button>
                </template>
                
                <!-- Caso contrário, mostra select -->
                <template v-else>
                  <select
                    :value="variable.value"
                    @change="handleVariableSelectChange(wIndex, variable.key, $event.target.value)"
                    class="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm dark:bg-slate-700 dark:border-slate-600"
                  >
                    <option value="">Selecione um valor</option>
                    <option
                      v-for="opt in variablePlaceholderOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </template>
              </div>
            </div>
          </div>

          <!-- Botão para adicionar novo canal -->
          <div v-if="availableWhatsappInboxes.length > 0" class="mt-4">
            <label class="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
              Adicionar canal WhatsApp
            </label>
            <div class="flex gap-2">
              <select
                v-model="selectedInboxToAdd"
                class="flex-1 px-3 py-2 border border-slate-300 rounded-lg dark:bg-slate-700 dark:border-slate-600"
              >
                <option value="">Selecione um canal</option>
                <option
                  v-for="inbox in availableWhatsappInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
              <Button
                variant="smooth"
                color="slate"
                size="sm"
                @click="addWhatsappTemplateConfig(selectedInboxToAdd)"
              >
                <fluent-icon icon="add" size="14" :icons="dashboardIcons" />
                Adicionar
              </Button>
            </div>
          </div>

          <!-- Mensagem quando não há mais canais disponíveis -->
          <p
            v-else-if="formData.whatsapp_templates.length > 0"
            class="text-sm text-slate-500 italic"
          >
            Todos os canais WhatsApp já estão configurados.
          </p>
        </div>

        <!-- Configuração de Webhook -->
        <div class="form-section">
          <div class="flex items-center justify-between mb-4">
            <h3 class="section-title mb-0">
              {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.WEBHOOK.TITLE') }}
            </h3>
            <WootToggle v-model="formData.webhook.enabled" />
          </div>

          <div v-if="formData.webhook.enabled" class="webhook-config">
            <WootInput
              v-model="formData.webhook.url"
              name="webhook_url"
              :label="t('KANBAN.MESSAGE_TEMPLATES.FORM.WEBHOOK.URL.LABEL')"
              :placeholder="
                t('KANBAN.MESSAGE_TEMPLATES.FORM.WEBHOOK.URL.PLACEHOLDER')
              "
              :error="errors.webhookUrl"
            />

            <WootSelect
              v-model="formData.webhook.method"
              name="webhook_method"
              :label="t('KANBAN.MESSAGE_TEMPLATES.FORM.WEBHOOK.METHOD.LABEL')"
              :options="httpMethods"
            />
          </div>
        </div>

        <!-- Condições -->
        <div class="form-section">
          <div class="flex items-center justify-between mb-4">
            <h3 class="section-title mb-0">
              {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.TITLE') }}
            </h3>
            <WootToggle v-model="formData.conditions.enabled" />
          </div>

          <div v-if="showConditionsEditor" class="conditions-config space-y-4">
            <div
              v-for="(rule, index) in formData.conditions.rules"
              :key="index"
              class="condition-rule flex items-start gap-2"
            >
              <div class="flex-1">
                <div class="space-y-2">
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{
                      t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.FIELD.LABEL')
                    }}
                  </label>
                  <select
                    :value="rule.field"
                    class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:border-slate-600"
                    @change="handleConditionFieldChange(rule, $event.target.value)"
                  >
                    <option value="" disabled>
                      {{
                        t(
                          'KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.FIELD.PLACEHOLDER'
                        )
                      }}
                    </option>
                    <option
                      v-for="field in availableFields"
                      :key="field.value"
                      :value="field.value"
                    >
                      {{ field.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="w-32">
                <div class="space-y-2">
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{
                      t(
                        'KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.OPERATOR.LABEL'
                      )
                    }}
                  </label>
                  <select
                    v-model="rule.operator"
                    class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:border-slate-600"
                  >
                    <option value="equals">Igual a</option>
                    <option value="contains">Contém</option>
                    <option value="starts_with">Começa com</option>
                    <option value="ends_with">Termina com</option>
                  </select>
                </div>
              </div>
              <div class="flex-1">
                <div class="space-y-2">
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{
                      t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.VALUE.LABEL')
                    }}
                  </label>
                  <!-- Dropdown para campo 'label' -->
                  <select
                    v-if="rule.field === 'label'"
                    v-model="rule.value"
                    class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:border-slate-600"
                  >
                    <option value="" disabled>Selecione uma etiqueta</option>
                    <option
                      v-for="labelOpt in labelOptions"
                      :key="labelOpt.value"
                      :value="labelOpt.value"
                    >
                      {{ labelOpt.label }}
                    </option>
                  </select>
                  <!-- Dropdown de agentes para campo agent_id -->
                  <select
                    v-else-if="rule.field === 'item_details.agent_id'"
                    v-model="rule.value"
                    class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:border-slate-600"
                  >
                    <option value="" disabled>Selecione um atendente</option>
                    <option
                      v-for="agentOpt in agentOptions"
                      :key="agentOpt.value"
                      :value="agentOpt.value"
                    >
                      {{ agentOpt.label }}
                    </option>
                  </select>
                  <!-- Input de texto para outros campos -->
                  <input
                    v-else
                    v-model="rule.value"
                    type="text"
                    class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:border-slate-600"
                    :placeholder="
                      t(
                        'KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.VALUE.PLACEHOLDER'
                      )
                    "
                  />
                </div>
              </div>
              <Button
                variant="ghost"
                color="ruby"
                size="xs"
                @click="removeConditionRule(index)"
              >
                <fluent-icon icon="delete" size="16" :icons="dashboardIcons" />
              </Button>
            </div>

            <Button
              variant="ghost"
              color="slate"
              size="xs"
              class="w-full"
              @click="addConditionRule"
            >
              <fluent-icon
                icon="add"
                size="16"
                class="mr-2"
                :icons="dashboardIcons"
              />
              {{ t('KANBAN.MESSAGE_TEMPLATES.FORM.CONDITIONS.ADD_RULE') }}
            </Button>
          </div>
        </div>

        <!-- Stage Notifications (WhatsApp Web + WhatsApp Official) -->
        <div v-if="hasNotificationInboxes" class="form-section">
          <div class="flex items-center justify-between mb-4">
            <h3 class="section-title mb-0">
              <fluent-icon
                icon="alert"
                size="18"
                class="inline-block mr-2 text-amber-500"
                :icons="dashboardIcons"
              />
              Notificações de Etapa
            </h3>
            <WootToggle
              v-model="formData.stage_notifications.enabled"
            />
          </div>

          <div
            v-if="formData.stage_notifications.enabled"
            class="notifications-config space-y-4"
          >
            <p class="text-sm text-slate-500 dark:text-slate-400">
              Envie notificações via WhatsApp para números externos quando um
              item entrar nesta etapa. Ideal para avisar gerentes, financeiro,
              etc.
            </p>

            <!-- Rate Limits (separate per type) -->
            <div class="flex flex-wrap items-center gap-4">
              <div v-if="wahaInboxes.length > 0" class="flex items-center gap-2">
                <label class="text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                  Limite/hora (WhatsApp Web):
                </label>
                <input
                  v-model.number="formData.stage_notifications.rate_limit_waha"
                  type="number"
                  min="0"
                  max="100"
                  class="w-16 px-2 py-1 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-woot-500 dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              <div v-if="whatsappOfficialInboxes.length > 0" class="flex items-center gap-2">
                <label class="text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                  Limite/hora (Oficial):
                </label>
                <input
                  v-model.number="formData.stage_notifications.rate_limit_official"
                  type="number"
                  min="0"
                  max="1000"
                  class="w-16 px-2 py-1 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-woot-500 dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              <span class="text-xs text-slate-400">0 = ilimitado</span>
            </div>

            <!-- Recipients List -->
            <div
              v-for="(recipient, rIndex) in formData.stage_notifications.recipients"
              :key="recipient.id"
              class="p-3 bg-white dark:bg-slate-700 rounded-lg space-y-3 border border-slate-200 dark:border-slate-600"
            >
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Destinatário {{ rIndex + 1 }}
                  <span
                    class="ml-2 px-1.5 py-0.5 text-[10px] rounded-full"
                    :class="recipient.type === 'whatsapp_official'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'"
                  >
                    {{ recipient.type === 'whatsapp_official' ? 'Oficial' : 'WhatsApp Web' }}
                  </span>
                </span>
                <Button
                  variant="ghost"
                  color="ruby"
                  size="xs"
                  @click="removeNotificationRecipient(rIndex)"
                >
                  <fluent-icon icon="delete" size="16" :icons="dashboardIcons" />
                </Button>
              </div>

              <!-- Type + Channel Row -->
              <div class="grid grid-cols-2 gap-3">
                <!-- Type Select -->
                <div>
                  <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                    Tipo
                  </label>
                  <select
                    :value="recipient.type || 'waha'"
                    class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 dark:bg-slate-700 dark:border-slate-600"
                    @change="handleRecipientTypeChange(rIndex, $event.target.value)"
                  >
                    <option
                      v-for="opt in notificationTypeOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>

                <!-- Channel Select (dynamic based on type) -->
                <div>
                  <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                    Canal
                  </label>
                  <select
                    v-model="recipient.inbox_id"
                    class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 dark:bg-slate-700 dark:border-slate-600"
                  >
                    <option :value="null" disabled>Selecione</option>
                    <option
                      v-for="inbox in getInboxesForType(recipient.type || 'waha')"
                      :key="inbox.id"
                      :value="inbox.id"
                    >
                      {{ inbox.name }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Phone Number with DDI (full width) -->
              <div>
                <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Número WhatsApp
                </label>
                <PhoneInput
                  :model-value="recipient.phone_number"
                  placeholder="999999999"
                  @update:model-value="onRecipientPhoneInput(rIndex, $event)"
                  @set-code="onRecipientSetCode(rIndex, $event)"
                />
              </div>

              <!-- Label -->
              <div>
                <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Nome / Identificação
                </label>
                <input
                  v-model="recipient.label"
                  type="text"
                  placeholder="Ex: Marcel (Gerente)"
                  class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 dark:bg-slate-700 dark:border-slate-600"
                />
              </div>

              <!-- WhatsApp Web: Free text message -->
              <div v-if="(recipient.type || 'waha') === 'waha'">
                <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Mensagem
                </label>
                <textarea
                  v-model="recipient.message"
                  rows="3"
                  placeholder="🎉 Nova venda! Cliente: {{contact.name}} Valor: {{item_details.value}}"
                  class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 dark:bg-slate-700 dark:border-slate-600 resize-none"
                />
                <div class="flex flex-wrap gap-1 mt-2">
                  <button
                    v-for="varOpt in variablePlaceholderOptions.filter(v => v.value !== 'MANUAL_INPUT')"
                    :key="varOpt.value"
                    type="button"
                    class="px-2 py-0.5 text-xs bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded hover:bg-woot-50 dark:hover:bg-woot-900 transition-colors cursor-pointer"
                    @click="insertNotificationVariable(rIndex, varOpt.value)"
                  >
                    {{ varOpt.label }}
                  </button>
                </div>
              </div>

              <!-- WhatsApp Official: Template selection -->
              <div v-if="recipient.type === 'whatsapp_official'">
                <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Template Oficial
                </label>
                <div class="flex items-center gap-2">
                  <div class="flex-1 px-3 py-2 text-sm bg-slate-100 dark:bg-slate-600 rounded-lg truncate">
                    {{ recipient.template_name || 'Nenhum template selecionado' }}
                    <span v-if="recipient.template_language" class="text-xs text-slate-400 ml-1">
                      ({{ recipient.template_language }})
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    color="slate"
                    size="xs"
                    @click="openNotifTemplatePicker(rIndex)"
                  >
                    Selecionar
                  </Button>
                </div>

                <!-- Variable Mapping for Official Template -->
                <div
                  v-if="getNotifRecipientVariables(rIndex).length > 0"
                  class="mt-3 space-y-2"
                >
                  <label class="block text-xs font-medium text-slate-600 dark:text-slate-400">
                    Variáveis do Template
                  </label>
                  <div
                    v-for="variable in getNotifRecipientVariables(rIndex)"
                    :key="variable.key"
                    class="flex items-center gap-2"
                  >
                    <span class="text-xs font-mono bg-slate-200 dark:bg-slate-600 px-2 py-1 rounded">
                      {{ formatVariableLabel(variable.key) }}
                    </span>
                    <span class="text-xs text-slate-400">→</span>
                    <!-- Manual input mode -->
                    <input
                      v-if="variable.value && variable.value.startsWith('manual:')"
                      type="text"
                      :value="variable.value.replace('manual:', '')"
                      placeholder="Valor fixo"
                      class="flex-1 px-2 py-1 text-xs border border-slate-300 rounded dark:bg-slate-700 dark:border-slate-600"
                      @input="updateNotifVariableMapping(rIndex, variable.key, 'manual:' + $event.target.value)"
                    />
                    <!-- Select mode -->
                    <select
                      v-else
                      :value="variable.value"
                      class="flex-1 px-2 py-1 text-xs border border-slate-300 rounded dark:bg-slate-700 dark:border-slate-600"
                      @change="handleNotifVariableSelectChange(rIndex, variable.key, $event.target.value)"
                    >
                      <option value="">Selecione um valor</option>
                      <option
                        v-for="opt in variablePlaceholderOptions"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <!-- Reset button for manual mode -->
                    <button
                      v-if="variable.value && variable.value.startsWith('manual:')"
                      type="button"
                      title="Voltar para seleção"
                      class="text-xs text-slate-400 hover:text-woot-500 cursor-pointer"
                      @click="updateNotifVariableMapping(rIndex, variable.key, '')"
                    >
                      ↩️
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add Recipient Button -->
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              class="w-full"
              @click="addNotificationRecipient"
            >
              <fluent-icon
                icon="add"
                size="16"
                class="mr-2"
                :icons="dashboardIcons"
              />
              Adicionar destinatário
            </Button>
          </div>
        </div>
      </div>

      <!-- Coluna Direita (Editor) -->
      <div class="right-column">
        <div class="editor-section">
          <WootMessageEditor
            v-model="formData.content"
            :placeholder="
              t('KANBAN.MESSAGE_TEMPLATES.FORM.CONTENT.PLACEHOLDER')
            "
            :error="errors.content"
            :enable-variables="true"
            :enable-suggestions="true"
            class="content-editor"
          />
        </div>
      </div>
    </div>

    <!-- Botões de Ação -->
    <div class="form-actions">
      <Button variant="ghost" color="slate" size="sm" @click="emit('close')">
        {{ t('KANBAN.ACTIONS.CANCEL') }}
      </Button>
      <Button variant="solid" color="blue" size="sm" type="submit">
        {{ t('KANBAN.ACTIONS.SAVE') }}
      </Button>
    </div>
  </form>

  <!-- Modal de seleção de template WhatsApp (main templates section) -->
  <div
    v-if="showTemplatePickerModal"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    @mousedown.stop
  >
    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
      <div class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
        <h3 class="text-lg font-medium text-slate-700 dark:text-slate-200">
          Selecionar Template WhatsApp
        </h3>
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          @click="showTemplatePickerModal = false"
        >
          <fluent-icon icon="dismiss" size="16" :icons="dashboardIcons" />
        </Button>
      </div>
      <div class="p-4 overflow-y-auto max-h-[60vh]">
        <TemplatesPicker
          v-if="currentWaTemplateIndex !== null && formData.whatsapp_templates[currentWaTemplateIndex]"
          :inbox-id="formData.whatsapp_templates[currentWaTemplateIndex].inbox_id"
          @on-select="onTemplateSelected"
        />
      </div>
    </div>
  </div>

  <!-- Modal de seleção de template WhatsApp (notifications section) -->
  <div
    v-if="showNotifTemplatePickerModal"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    @mousedown.stop
  >
    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
      <div class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
        <h3 class="text-lg font-medium text-slate-700 dark:text-slate-200">
          Selecionar Template (Notificação)
        </h3>
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          @click="showNotifTemplatePickerModal = false"
        >
          <fluent-icon icon="dismiss" size="16" :icons="dashboardIcons" />
        </Button>
      </div>
      <div class="p-4 overflow-y-auto max-h-[60vh]">
        <TemplatesPicker
          v-if="currentNotifRecipientIndex !== null && formData.stage_notifications.recipients[currentNotifRecipientIndex]"
          :inbox-id="formData.stage_notifications.recipients[currentNotifRecipientIndex].inbox_id"
          @on-select="onNotifTemplateSelected"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.message-template-form {
  @apply space-y-4;
}

.form-grid {
  @apply grid grid-cols-2 gap-4;
}

.left-column {
  @apply space-y-4;
}

.right-column {
  @apply sticky top-0;
  @apply border border-dashed border-woot-500 rounded-lg;
}

.form-section {
  @apply px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg;
}

.editor-section {
  @apply h-full bg-slate-50 dark:bg-slate-800 rounded-lg p-4;
  min-height: calc(100vh - 400px);
}

.section-title {
  @apply text-base font-medium text-slate-700 dark:text-slate-300 mb-2;
}

.webhook-config,
.conditions-config,
.notifications-config {
  @apply mt-4;
}

.form-actions {
  @apply flex justify-end gap-2 pt-4;
}

.condition-rule {
  @apply p-3 bg-white dark:bg-slate-700 rounded-lg;
}

.content-editor {
  @apply h-full;
  :deep(.woot-editor) {
    @apply border border-dashed border-woot-500 rounded-lg;
    @apply p-2 bg-white dark:bg-slate-700;
    height: calc(100vh - 450px);
  }

  // Force variable/mention dropdown to open BELOW the editor
  // instead of above (default bottom-full goes off-screen in modals)
  :deep(.mention--box) {
    bottom: auto !important;
    top: 0 !important;
  }
}

.error-message {
  @apply text-sm text-n-ruby-9;
}

.loading-state {
  @apply text-sm text-slate-600 dark:text-slate-400;
}
</style>
